import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOfferListFilterContext } from './components/offer-list-filter-context';
import { ProductContext } from './components/product-context';

import { useAppContext } from 'src/components/app-context';
import loadable from '@loadable/component';
import { Panel } from '@prisjakt/ui/panel';
import { Lazy } from 'src/components/lazy';
import { ScrollElement } from 'src/components/scroll';
import {
  getAllOffersAsArray,
  filterOffers,
} from 'src/components/price-list/filtering';
import { PageContent, PageSection } from 'src/components/page';
import AdContainer from '../../components/ads';
import RecentlyVisited from '../../components/recently-visited';
import { ProductPageHeader } from './components/product-header';
import { ProductTabs } from './components/product-tabs';
import ProductDuringPromoBanner from './components/product-promo-banner/product-during-promo-banner';
import { ProductBreadCrumb } from './components/product-breadcrumb';
import ProductBeforePromoBanner from './components/product-promo-banner/product-before-promo-banner';
import ProductScrollTopButton from './components/product-scroll-top-button';
import ProductCampaignDeals from './components/product-campaign-deals';
import { ProductNavigation } from './components/product-section-navigation';
import { MobileTopAd } from './components/price-list/ads/mobile-top-ad';

import { AppState } from 'src/reducers';

import { hideModal } from 'src/components/modal/actions';

import { getCurrentCampaign, fetchDeals } from './utils';

import { PRODUCT_ALERT_MODAL_ID } from './constants';

import { Campaign, PriceList, Product } from 'src/types';
import { CurrentCampaign, ProductPageData, RouteSection } from './types';

import { ProductPage as StyledProductPage, Modal } from './styles';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

function mapAlternativeOffersWithUniqueShipping(product: Product): PriceList {
  return {
    ...product.prices,
    nodes: getAllOffersAsArray(product.prices).map(offer => ({
      ...offer,
      alternativePrices: offer.alternativePrices?.map(altOffer => ({
        ...altOffer,
        shipping: altOffer?.shopHasUniqueShippings
          ? altOffer.shipping
          : offer.shipping,
      })),
    })),
  };
}

type Props = {
  activeCampaign: Campaign;
  data: ProductPageData;
  routeSection: RouteSection;
};

export const ProductPage = ({
  activeCampaign,
  data,
  routeSection = 'main',
}: Props) => {
  const { market } = useAppContext();
  const { getActiveFilters } = useOfferListFilterContext();

  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);

  const { product, recommendations, prismicArticles } = data;

  const currentModal = useSelector((state: AppState) => state.modal.current);
  const campaignBanners = useSelector(
    (state: AppState) => state.globals.campaignBanners
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    fetchDeals(market, product.id)
      .then(
        res =>
          res.hits?.hits &&
          setCampaignsState(
            res.hits.hits.map((hit: any) => ({
              id: hit._source.campaign.id,
              price: {
                id: hit._source.priceInfo.priceId.toString(),
              },
            }))
          )
      )
      .catch(() => {
        /* Fail silently */
      });
  }, [product && product.id]); // eslint-disable-line

  product.campaigns = campaigns;

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const currentCampaign = getCurrentCampaign(
    activeCampaign,
    product
  ) as CurrentCampaign;

  const activeFilters = getActiveFilters();
  const activeAdditiveFilters = activeFilters.filter(filter =>
    Boolean(filter.additive)
  );

  const productPrices = useMemo(
    () => mapAlternativeOffersWithUniqueShipping(product),
    [product]
  );

  const filteredOffers = filterOffers(activeFilters, productPrices);

  const allOffersWithActiveAdditives = filterOffers(
    activeAdditiveFilters,
    productPrices
  );

  const allOffers = useMemo(() => getAllOffersAsArray(productPrices), [
    productPrices,
  ]);

  return (
    <ProductContext.Provider
      value={{
        product,
        offers: { filteredOffers },
        allOffers,
        allOffersWithActiveAdditives,
        priceInfoArticles: prismicArticles?.nodes,
      }}
    >
      <PageContent withAds>
        <StyledProductPage>
          {currentModal === PRODUCT_ALERT_MODAL_ID && (
            <Modal
              id={PRODUCT_ALERT_MODAL_ID}
              onRequestClose={() => dispatch(hideModal())}
              width={500}
            >
              <ProductAlertModalContent
                productId={product.id}
                hideModal={() => dispatch(hideModal())}
                privacyPolicyUri={privacyPolicyUri || undefined}
              />
            </Modal>
          )}
          <ProductDuringPromoBanner
            product={product}
            campaignBanners={campaignBanners}
          />

          <MobileTopAd />

          <ProductBreadCrumb product={product} />
          <ScrollElement name="Top" />
          <ProductPageHeader
            product={product}
            isHeaderVisible={isHeaderVisible}
            setHeaderVisible={setHeaderVisible}
            currentCampaign={currentCampaign}
          />
          <ProductScrollTopButton />
          <ProductNavigation />
          <div>
            <ProductTabs
              routeSection={routeSection}
              product={product}
              recommendations={recommendations}
              currentCampaign={currentCampaign}
              trendingProducts={product.trendingProducts}
            />

            <Panel className="post-tabs-ad">
              <AdContainer
                placement="panorama_target"
                format="panorama_3_wde"
                min={861}
                max={10000}
                scalable
                scaleBase=".post-tabs-ad"
                triggerPoint={100}
              />
            </Panel>

            <ProductCampaignDeals product={product} />

            <section>
              <Lazy offset={100}>
                <PageSection recessed>
                  <RecentlyVisited offset={1} />
                </PageSection>
              </Lazy>
            </section>

            <ProductBeforePromoBanner campaignBanners={campaignBanners} />
          </div>
        </StyledProductPage>
      </PageContent>
    </ProductContext.Provider>
  );
};
